// Pure client-side share estimator for the approximated `quantiles` insight (ACT2-531).
//
// A cumulative F(x) — piecewise-linear over a quantile ladder, with min/max as the q0/q1 boundaries —
// evaluated against the leaf's operator + resolved numeric bounds. It interpolates over ~6–8 knots, so
// every number it returns is an ESTIMATE: it assumes a uniform spread inside each band, which is false
// in general and badly false when a boundary repeats (e.g. q05 === min, a mass sitting at 0). Zero
// warehouse queries — the arithmetic is entirely client-side.
//
// Two honesty rules it enforces (ticket §"Honesty rules"): fewer than two usable knots ⇒ no estimate
// (returns null, and the caller shows no readout); and it never mutates or commits a value — callers
// read its number to label an input, they do not write it back into the predicate.

/**
 * A cumulative-distribution knot. `q` is the RIGHT-continuous level F(value) = P(X ≤ value); `leftQ` is
 * the left limit F(value⁻) = P(X < value). They differ only at a coalesced plateau (a point mass), where
 * `q` is the run's highest quantile level and `leftQ` its lowest; for a lone knot `leftQ === q`.
 */
export interface QuantileKnot {
  q: number;
  value: number;
  leftQ: number;
}

/** The numeric operators the slider surface offers, already normalised (see distribution-domains). */
export type ShareOperator = 'lt' | 'lte' | 'gt' | 'gte' | 'btw' | 'nbtw';

/** The resolved handle position(s), as the domain reads them off the predicate value. */
export interface ShareBounds {
  single?: number | null;
  lo?: number | null;
  hi?: number | null;
}

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Assemble the CDF knots from the probed ladder plus the observed extremes, made strictly increasing
 * in value — the one property the interpolation needs.
 *
 * `min`/`max` are injected as the q0/q1 anchors so the tails are grounded rather than guessed. The
 * ladder can be non-monotonic in value (min/max come from a different probe pass than the quantiles,
 * and a field-level last-write-wins merge can cross them), so we sort by `q`, coalesce an equal-valued
 * run (a plateau is a point mass), and drop any knot whose value goes backwards. Fewer than two
 * survivors ⇒ `[]`, which every caller reads as "no estimate".
 *
 * A coalesced run keeps BOTH ends: `q` advances to the run's HIGHEST level so F(x) = P(X ≤ x) stays
 * right-continuous, and `leftQ` retains its LOWEST level so the left limit P(X < x) survives too. Both
 * are load-bearing — `lt`/`gte` and `BETWEEN`'s lower edge read the left limit, and dropping it reports a
 * point mass on the wrong side (a zero-inflated column read "≥ 0" as 75% instead of 100%).
 */
export function buildKnots(
  quantiles: readonly { q: number; value: number }[],
  min?: number,
  max?: number,
): QuantileKnot[] {
  const raw: { q: number; value: number }[] = [];
  for (const knot of quantiles) {
    if (isFiniteNumber(knot.q) && isFiniteNumber(knot.value)) raw.push({ q: knot.q, value: knot.value });
  }
  if (isFiniteNumber(min)) raw.push({ q: 0, value: min });
  if (isFiniteNumber(max)) raw.push({ q: 1, value: max });
  raw.sort((a, b) => a.q - b.q);

  const knots: QuantileKnot[] = [];
  for (const knot of raw) {
    const prev = knots[knots.length - 1];
    if (prev === undefined || knot.value > prev.value) {
      knots.push({ q: knot.q, value: knot.value, leftQ: knot.q });
    } else if (knot.value === prev.value) {
      // A repeated value is a plateau. Advance `q` to the run's HIGHEST level (right-continuous P(X ≤ x))
      // but leave `leftQ` at the LOWEST level pushed with the run's first knot (the left limit P(X < x)).
      prev.q = knot.q;
    }
    // knot.value < prev.value ⇒ the ladder crossed under a LWW merge; drop the backwards knot.
  }
  return knots.length >= 2 ? knots : [];
}

/** Which limit of F to read AT a knot value: the right-continuous P(X ≤ x) or the left limit P(X < x). */
type CdfSide = 'left' | 'right';

/**
 * F(x): the interpolated share of NON-NULL values below `x` (`side: 'left'`, P(X < x)) or at-or-below it
 * (`side: 'right'`, P(X ≤ x)), in [0,1].
 *
 * The two sides differ ONLY at a value carrying a point mass (a coalesced plateau): the right limit
 * includes the mass (`q`), the left limit excludes it (`leftQ`). Strictly between two distinct values F
 * is continuous, so the interpolation is shared — rising from the TOP of the lower knot's plateau (`lo.q`)
 * to the BOTTOM of the upper knot's (`hi.leftQ`). Assumes at least two knots — callers gate on
 * `buildKnots` returning a non-empty array first.
 */
function cdf(knots: readonly QuantileKnot[], x: number, side: CdfSide): number {
  const first = knots[0];
  const last = knots[knots.length - 1];
  if (first === undefined || last === undefined) return 0;
  // Below/above the measured range both limits agree (0, or the top level `last.q`). The left/right split
  // only bites AT a knot value carrying a point mass. The minimum (knots[0]) is only ever a `lo` inside the
  // loop, so it must be special-cased here; every other knot — interior AND the maximum — is caught as a
  // `hi` by the loop's `x === hi.value` branch below, so it needs no separate endpoint case.
  if (x < first.value) return 0;
  if (x === first.value) return side === 'right' ? first.q : first.leftQ;
  if (x > last.value) return last.q;
  for (let i = 0; i < knots.length - 1; i += 1) {
    const lo = knots[i];
    const hi = knots[i + 1];
    if (lo === undefined || hi === undefined) continue;
    if (x === hi.value) return side === 'right' ? hi.q : hi.leftQ;
    if (x > lo.value && x < hi.value) {
      const span = hi.value - lo.value;
      return span <= 0 ? lo.q : lo.q + ((hi.leftQ - lo.q) * (x - lo.value)) / span;
    }
  }
  return last.q; // unreachable given the bounds checks above; keeps the function total
}

/** F(x) = P(X ≤ x), right-continuous. The historical public evaluator; `selectedShare` picks the side. */
export function cumulativeShare(knots: readonly QuantileKnot[], x: number): number {
  return cdf(knots, x, 'right');
}

/**
 * The share of NON-NULL values the (operator, bounds) selects, in [0,1] — or `null` when it cannot be
 * estimated (no usable ladder, or the value the operator needs is unset / non-finite).
 *
 * The operator picks which limit of F to read, so a point mass lands on the side the warehouse's own
 * `<` / `<=` / `>` / `>=` / inclusive `BETWEEN` semantics put it:
 *   `lt`  = F(x⁻) = P(X < x)      (left limit)
 *   `lte` = F(x)  = P(X ≤ x)      (right-continuous)
 *   `gt`  = 1 − F(x)  = P(X > x)  (right-continuous)
 *   `gte` = 1 − F(x⁻) = P(X ≥ x)  (left limit)
 *   `btw` = F(hi) − F(lo⁻)        (right upper edge, left lower edge); `nbtw` = its complement.
 * The left/right split only changes the answer at a coalesced plateau; on a continuous stretch the two
 * limits coincide. Getting it wrong reads a zero-inflated column's "≥ 0" as 75% instead of 100%, or
 * `BETWEEN 0 AND 0` as 0% instead of the plateau's own mass.
 */
export function selectedShare(
  knots: readonly QuantileKnot[],
  operator: ShareOperator,
  bounds: ShareBounds,
): number | null {
  if (knots.length < 2) return null;
  const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
  switch (operator) {
    case 'lt':
      return isFiniteNumber(bounds.single) ? clamp01(cdf(knots, bounds.single, 'left')) : null;
    case 'lte':
      return isFiniteNumber(bounds.single) ? clamp01(cdf(knots, bounds.single, 'right')) : null;
    case 'gt':
      return isFiniteNumber(bounds.single) ? clamp01(1 - cdf(knots, bounds.single, 'right')) : null;
    case 'gte':
      return isFiniteNumber(bounds.single) ? clamp01(1 - cdf(knots, bounds.single, 'left')) : null;
    case 'btw':
    case 'nbtw': {
      if (!isFiniteNumber(bounds.lo) || !isFiniteNumber(bounds.hi)) return null;
      // A reversed range (lo > hi) compiles to `BETWEEN lo AND hi`, which matches zero rows — the
      // backend refuses to emit one for exactly that reason. Go quiet rather than silently reordering it
      // into a confident number.
      if (bounds.lo > bounds.hi) return null;
      // Inclusive on both ends: P(lo ≤ X ≤ hi) = P(X ≤ hi) − P(X < lo). Coincident edges (lo === hi) then
      // read the plateau's own mass F(v) − F(v⁻), not zero.
      const inside = clamp01(cdf(knots, bounds.hi, 'right') - cdf(knots, bounds.lo, 'left'));
      return operator === 'btw' ? inside : clamp01(1 - inside);
    }
    default: {
      const _exhaustive: never = operator;
      return _exhaustive;
    }
  }
}

/**
 * Scale a non-null share to a share of ALL entities using the column's fill rate. Returns `null` when
 * the fill rate is unknown — the caller then shows the non-null share and labels it as such, never
 * silently relabelling it as a share of everyone.
 */
export function shareOfAll(nonNullShare: number, nonNullFraction?: number): number | null {
  return isFiniteNumber(nonNullFraction) ? nonNullShare * nonNullFraction : null;
}
